import React, { useCallback, useState } from "react";

interface AccordionTemplateProps {
    title?: string;
    children: React.ReactNode;
    className?: string;
    accordionName?: string;
    defaultChecked?: boolean;
    onEdit?: () => void;
    dataTestId?: string;
}

/**
 * Closes all other accordions in the same group
 */
const closeOtherAccordions = (accordionName: string, currentInput: HTMLInputElement | null): void => {
    const allAccordions = document.querySelectorAll<HTMLInputElement>(`input[name="${accordionName}"]`);

    allAccordions.forEach((accordion) => {
        const isDifferentAccordion = accordion !== currentInput;
        if (isDifferentAccordion) {
            accordion.checked = false;
        }
    });
};

const AccordionTemplate = ({
    title,
    children,
    className = "w-[370px] tablet:w-[448px]",
    accordionName,
    defaultChecked = false,
    onEdit,
    dataTestId,
}: AccordionTemplateProps) => {
    // ========== State ==========
    const [isExpanded, setIsExpanded] = useState(defaultChecked);

    const titleId = accordionName ? `${accordionName}-title` : undefined;

    /**
     * Ensures only one accordion is open at a time within the same group
     */
    const handleAccordionChange = useCallback(
        (e: Event) => {
            const target = e.target as HTMLInputElement;

            setIsExpanded(target.checked);

            if (accordionName && target.checked) {
                closeOtherAccordions(accordionName, target);
            }
        },
        [accordionName]
    );

    const accordionInputRef = useCallback(
        (input: HTMLInputElement | null) => {
            if (!accordionName || !input) {
                return;
            }

            input.addEventListener("change", handleAccordionChange);
            return () => {
                input.removeEventListener("change", handleAccordionChange);
            };
        },
        [accordionName, handleAccordionChange]
    );

    return (
        <div className={`flex flex-col gap-4 overflow-hidden ${className}`}>
            <form data-testid={dataTestId ? `${dataTestId}-form` : undefined} onSubmit={(e) => e.preventDefault()}>
                <div
                    className="collapse collapse-arrow bg-base-200 has-[:checked]:bg-base-100"
                    role="region"
                    aria-labelledby={titleId}
                >
                    <input
                        ref={accordionInputRef}
                        type="checkbox"
                        name={accordionName}
                        defaultChecked={defaultChecked}
                        data-testid={dataTestId}
                        aria-labelledby={titleId}
                        aria-expanded={isExpanded}
                    />
                    <div
                        id={titleId}
                        className="collapse-title text-lg font-bold text-base-content"
                        data-testid={dataTestId ? `${dataTestId}-title` : undefined}
                    >
                        {title}
                    </div>
                    <div className="collapse-content">
                        {children}
                        {onEdit && (
                            <button
                                type="button"
                                className="text-info-content hover:underline text-sm font-bold"
                                onClick={onEdit}
                            >
                                Edit
                            </button>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
};

export default AccordionTemplate;
